import { useRole } from '@/hooks/useRole'
import { useEffect, useState, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Search, LayoutDashboard, Activity, FileText, GitBranch,
  Bell, Sparkles, Settings, BarChart3, Plus, Clock,
  ArrowRight, Puzzle, Users, Command
} from 'lucide-react'
import { cn } from '@/utils/cn'
import { useAppStore } from '@/store/app'

interface CommandItem {
  id: string
  label: string
  description?: string
  icon: React.ElementType
  action: () => void
  shortcut?: string
  group: string
}

export function CommandPalette() {
  const navigate = useNavigate()
  const { commandPaletteOpen, closeCommandPalette } = useAppStore()
  const [query, setQuery] = useState('')
  const [selectedIndex, setSelectedIndex] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  const { canAccessPath, isWasteRestricted } = useRole()
  const ALL_COMMANDS: CommandItem[] = [
    // Navigation
    { id: 'nav-home',       label: 'Go to Home',           icon: LayoutDashboard, group: 'Navigate', action: () => navigate('/'),                shortcut: 'G H' },
    { id: 'nav-dashboards', label: 'Go to Dashboards',     icon: LayoutDashboard, group: 'Navigate', action: () => navigate('/dashboards'),      shortcut: 'G D' },
    { id: 'nav-metrics',    label: 'Go to Metrics',        icon: Activity,        group: 'Navigate', action: () => navigate('/metrics'),         shortcut: 'G M' },
    { id: 'nav-logs',       label: 'Go to Logs',           icon: FileText,        group: 'Navigate', action: () => navigate('/logs'),            shortcut: 'G L' },
    { id: 'nav-traces',     label: 'Go to Traces',         icon: GitBranch,       group: 'Navigate', action: () => navigate('/traces'),          shortcut: 'G T' },
    { id: 'nav-alerts',     label: 'Go to Alerts',         icon: Bell,            group: 'Navigate', action: () => navigate('/alerts/rules'),    shortcut: 'G A' },
    { id: 'nav-ai',         label: 'Go to AI Insights',    icon: Sparkles,        group: 'Navigate', action: () => navigate('/ai'),              shortcut: 'G I' },
    { id: 'nav-reports',    label: 'Go to Reports',        icon: BarChart3,       group: 'Navigate', action: () => navigate('/reports'),         shortcut: 'G R' },
    { id: 'nav-plugins',    label: 'Go to Plugins',        icon: Puzzle,          group: 'Navigate', action: () => navigate('/plugins') },
    { id: 'nav-settings',   label: 'Go to Settings',       icon: Settings,        group: 'Navigate', action: () => navigate('/settings'),        shortcut: 'G S' },
    // Create
    { id: 'new-dashboard',  label: 'New Dashboard',        icon: Plus,            group: 'Create',   action: () => navigate('/dashboards/new') },
    { id: 'new-alert',      label: 'New Alert Rule',       icon: Plus,            group: 'Create',   action: () => navigate('/alerts/rules/new') },
    { id: 'new-report',     label: 'New Report',           icon: Plus,            group: 'Create',   action: () => navigate('/reports/new') },
    { id: 'ai-dashboard',   label: 'Generate Dashboard with AI', icon: Sparkles,  group: 'Create',   action: () => navigate('/dashboards/generate') },
    // Time range
    { id: 'time-5m',  label: 'Last 5 minutes',  icon: Clock, group: 'Time Range', action: () => {}, shortcut: 'T 5' },
    { id: 'time-1h',  label: 'Last 1 hour',     icon: Clock, group: 'Time Range', action: () => {}, shortcut: 'T 1' },
    { id: 'time-24h', label: 'Last 24 hours',   icon: Clock, group: 'Time Range', action: () => {}, shortcut: 'T 2' },
    { id: 'time-7d',  label: 'Last 7 days',     icon: Clock, group: 'Time Range', action: () => {}, shortcut: 'T 7' },
  ]

  const availableCommands: CommandItem[] = isWasteRestricted
    ? [{ id: 'nav-waste', label: 'Go to Waste Management', icon: LayoutDashboard, group: 'Navigate', action: () => navigate('/waste') }].filter(() => canAccessPath('/waste'))
    : ALL_COMMANDS

  const filtered = query.trim()
    ? availableCommands.filter(cmd =>
        cmd.label.toLowerCase().includes(query.toLowerCase()) ||
        cmd.description?.toLowerCase().includes(query.toLowerCase()) ||
        cmd.group.toLowerCase().includes(query.toLowerCase())
      )
    : availableCommands

  const grouped = filtered.reduce<Record<string, CommandItem[]>>((acc, cmd) => {
    if (!acc[cmd.group]) acc[cmd.group] = []
    acc[cmd.group].push(cmd)
    return acc
  }, {})

  const flatList = Object.values(grouped).flat()

  const execute = useCallback((cmd: CommandItem) => {
    cmd.action()
    closeCommandPalette()
    setQuery('')
    setSelectedIndex(0)
  }, [closeCommandPalette])

  useEffect(() => {
    if (commandPaletteOpen) {
      setQuery('')
      setSelectedIndex(0)
      setTimeout(() => inputRef.current?.focus(), 50)
    }
  }, [commandPaletteOpen])

  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (!commandPaletteOpen) return

      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setSelectedIndex(i => Math.min(i + 1, flatList.length - 1))
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setSelectedIndex(i => Math.max(i - 1, 0))
      } else if (e.key === 'Enter') {
        e.preventDefault()
        const cmd = flatList[selectedIndex]
        if (cmd) execute(cmd)
      } else if (e.key === 'Escape') {
        closeCommandPalette()
      }
    }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [commandPaletteOpen, flatList, selectedIndex, execute, closeCommandPalette])

  // Scroll selected item into view
  useEffect(() => {
    const el = listRef.current?.querySelector('[data-selected="true"]')
    el?.scrollIntoView({ block: 'nearest' })
  }, [selectedIndex])

  let globalIndex = 0

  return (
    <AnimatePresence>
      {commandPaletteOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 backdrop-blur-sm"
            onClick={closeCommandPalette}
          />

          {/* Palette */}
          <motion.div
            initial={{ opacity: 0, y: -20, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.97 }}
            transition={{ duration: 0.15, ease: [0, 0, 0.2, 1] }}
            className="fixed top-[15vh] left-1/2 -translate-x-1/2 w-full max-w-xl z-50"
          >
            <div className="bg-bg-overlay border border-border-default rounded-xl shadow-2xl overflow-hidden">
              {/* Input */}
              <div className="flex items-center gap-3 px-4 py-3 border-b border-border-default">
                <Search className="w-4 h-4 text-text-tertiary flex-shrink-0" />
                <input
                  ref={inputRef}
                  value={query}
                  onChange={e => { setQuery(e.target.value); setSelectedIndex(0) }}
                  placeholder="Search or type a command..."
                  className="flex-1 bg-transparent text-text-primary placeholder:text-text-tertiary text-sm outline-none"
                />
                <kbd className="text-[10px] font-mono text-text-tertiary bg-bg-muted border border-border-default rounded px-1.5 py-0.5">
                  ESC
                </kbd>
              </div>

              {/* Results */}
              <div ref={listRef} className="max-h-96 overflow-y-auto py-2">
                {Object.entries(grouped).map(([group, cmds]) => (
                  <div key={group}>
                    <div className="px-4 py-1.5">
                      <span className="text-[10px] font-semibold text-text-tertiary uppercase tracking-wider">
                        {group}
                      </span>
                    </div>
                    {cmds.map(cmd => {
                      const idx = globalIndex++
                      const isSelected = idx === selectedIndex
                      const Icon = cmd.icon
                      return (
                        <button
                          key={cmd.id}
                          data-selected={isSelected}
                          onClick={() => execute(cmd)}
                          onMouseEnter={() => setSelectedIndex(idx)}
                          className={cn(
                            'w-full flex items-center gap-3 px-4 py-2 text-sm transition-colors text-left',
                            isSelected
                              ? 'bg-brand-500/15 text-text-primary'
                              : 'text-text-secondary hover:bg-bg-subtle'
                          )}
                        >
                          <Icon className={cn('w-4 h-4 flex-shrink-0', isSelected ? 'text-brand-400' : 'text-text-tertiary')} />
                          <span className="flex-1">{cmd.label}</span>
                          {cmd.shortcut && (
                            <kbd className="text-[10px] font-mono text-text-tertiary">
                              {cmd.shortcut}
                            </kbd>
                          )}
                          {isSelected && <ArrowRight className="w-3.5 h-3.5 text-brand-400" />}
                        </button>
                      )
                    })}
                  </div>
                ))}

                {filtered.length === 0 && (
                  <div className="px-4 py-8 text-center">
                    <p className="text-sm text-text-secondary">No results for "{query}"</p>
                    <p className="text-xs text-text-tertiary mt-1">Try searching for a page, dashboard, or action</p>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="border-t border-border-default px-4 py-2 flex items-center gap-4 text-[11px] text-text-tertiary">
                <span className="flex items-center gap-1">
                  <kbd className="font-mono">↑↓</kbd> navigate
                </span>
                <span className="flex items-center gap-1">
                  <kbd className="font-mono">↵</kbd> select
                </span>
                <span className="flex items-center gap-1">
                  <kbd className="font-mono">esc</kbd> close
                </span>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}
